import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { Case, Loader, Switch } from "@features/ui";
import { DashLayout } from "@features/ui/layout";
import { ErrorToast, Toast, ToastType } from "@features/ui/toast";
import { useAuth } from "@hooks/use-auth";
import { useEventTracking } from "@hooks/use-event-tracking";
import { isSsoEnabled } from "@lib/app-features.helpers";

import { LoginForm } from "./components/login-form";

import "./index.scss";

const cleverIdpUrl = import.meta.env.CLEVER_IDP_URL;
const classLinkIdpUrl = import.meta.env.CLASSLINK_IDP_URL;
const nycDoeIdpUrl = import.meta.env.NYCDOE_IDP_URL;
// const ssoLoginUrl = import.meta.env.SSO_LOGIN_URL;

const resetPasswordMessage = "Your password has been updated successfully.";
const expiredSessionMessage = (
  <div>
    Your session has expired.
    <br />
    Please try logging in again.
  </div>
);

// Leaving this hook here for now because it's a bit of a hack and ideally we
// will NOT use this anywhere else.
const _useIsFirstStrictModeRender = () => {
  const renderCount = useRef(0);
  const [isFirstRender, setIsFirstRender] = useState(import.meta.env.DEV);

  useEffect(() => {
    renderCount.current++;
    setIsFirstRender(import.meta.env.DEV && renderCount.current === 1);
  }, []);

  return isFirstRender;
};

export const LoginPage = () => {
  const isFirstStrictModeRender = _useIsFirstStrictModeRender();
  const [openError, setOpenError] = useState(false);

  const [loginMethod, setLoginMethod] = useState(isSsoEnabled() ? "" : "email");
  const [ssoWaiting, setSsoWaiting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  const { mutateAsync: trackEvent } = useEventTracking();
  const { login, loading, error } = useAuth();

  const [toastConfig, setToastConfig] = useState();

  useEffect(() => {
    setOpenError(!!error);
  }, [error]);

  useEffect(() => {
    // When running in strict mode the component will render twice. This is fine
    // for almost everything, but if a sessionId has been provided in the URL we
    // need to wait until the second render to avoid the session being deleted
    // before the second render tries to use it. This is probably a sign that we
    // shouldn't be consuming query params like this, but doing that and making
    // it work with the useAuth hook is a bigger refactor.
    if (isFirstStrictModeRender) {
      return;
    }

    if (queryParams.has("event_group_id")) {
      trackEvent({
        event_group_id: queryParams.get("event_group_id"),
        type: "engaged",
      });
    }
    if (queryParams.has("it")) {
      login({ impersonationToken: queryParams.get("it") });
      return;
    }
    if (queryParams.has("sessionId")) {
      login({ sessionId: queryParams.get("sessionId") });
      return;
    }
    if (isSsoEnabled() && queryParams.has("sso")) {
      login({ ssoToken: queryParams.get("sso") });
      return;
    }

    if (location.state?.passwordReset) {
      setToastConfig({
        type: ToastType.success,
        message: resetPasswordMessage,
      });
    } else if (location.state?.userLoginExpired) {
      setToastConfig({
        type: ToastType.error,
        message: expiredSessionMessage,
      });
    }

    const timeoutId = setTimeout(() => {
      setToastConfig();
      navigate("/login", { replace: true });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [isFirstStrictModeRender]);

  const handleLogin = async (e, email, password) => {
    e.preventDefault();

    await login({ email, password });
  };

  return (
    <DashLayout className="login-page">
      <Toast
        open={!!toastConfig}
        message={toastConfig?.message}
        type={toastConfig?.type}
      />
      <ErrorToast
        open={openError}
        onClose={() => setOpenError()}
        message={error?.detail}
        closable
      />

      <Switch option={loginMethod}>
        <Case value="">
          {loading || ssoWaiting ? (
            <Loader show text="Logging in..." />
          ) : (
            <div className="form-options">
              <Button
                primary
                className="login-button login-email"
                onClick={() => setLoginMethod("email")}
              >
                Sign in with Username or Email
              </Button>
              <div className="login-separator">
                <div />
                <div>OR</div>
                <div />
              </div>
              <div className="sso-options">
                <Button
                  className="login-button login-clever"
                  onClick={() => {
                    setSsoWaiting(true);
                    window.location.href = cleverIdpUrl;
                  }}
                >
                  <img
                    src="/images/sso/clever.svg"
                    alt="Clever Logo"
                    aria-hidden
                  />
                  <span>Sign in with Clever</span>
                </Button>
                <Button
                  className="login-button login-classlink"
                  onClick={() => {
                    setSsoWaiting(true);
                    window.location.href = classLinkIdpUrl;
                  }}
                >
                  <img
                    src="/images/sso/classlink.svg"
                    alt="ClassLink Logo"
                    aria-hidden
                  />
                  <span>Sign in with ClassLink</span>
                </Button>
                <Button
                  className="login-button login-nyc-doe"
                  onClick={() => {
                    setSsoWaiting(true);
                    window.location.href = nycDoeIdpUrl;
                  }}
                >
                  <img
                    src="/images/sso/nyc-doe.png"
                    alt="NYC DOE Logo"
                    aria-hidden
                  />
                  <span>Sign in with NYC DOE</span>
                </Button>
              </div>
            </div>
          )}
        </Case>
        <Case value="email">
          <LoginForm
            loading={loading}
            onSignIn={handleLogin}
            onResetLogin={() => setLoginMethod("")}
          />
        </Case>
      </Switch>
    </DashLayout>
  );
};
